/**
 * MirrorDockContext -- State provider for the floating mirror player dock.
 *
 * Lives inside StreamingLayout so the dock persists across streaming
 * subpage navigation (Dashboard, Control Center, etc.).
 */
import { createContext, useCallback, useContext, useState } from "react";

import type { MirrorMetadata } from "../hooks/useMirrorSession";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MirrorDockState {
  mirrorId: string;
  token: string;
  expiresAt: number;
  metadata: MirrorMetadata;
  /**
   * WSD session ID of the source user being mirrored.
   * Stored so the dock can re-call `/mirror/start` on a drift rebase
   * without requiring the caller to track this separately.
   */
  sessionId: string;
  /** Internal user ID of the source user being mirrored */
  targetUserId: string;
}

interface MirrorDockContextValue {
  dock: MirrorDockState | null;
  openDock: (state: MirrorDockState) => void;
  closeDock: () => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const MirrorDockCtx = createContext<MirrorDockContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const MirrorDockProvider = ({ children }: { children: React.ReactNode }) => {
  const [dock, setDock] = useState<MirrorDockState | null>(null);

  const openDock = useCallback((state: MirrorDockState) => {
    setDock(state);
  }, []);

  const closeDock = useCallback(() => {
    setDock(null);
  }, []);

  return (
    <MirrorDockCtx.Provider value={{ dock, openDock, closeDock }}>
      {children}
    </MirrorDockCtx.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useMirrorDock(): MirrorDockContextValue {
  const ctx = useContext(MirrorDockCtx);
  if (!ctx) {
    throw new Error("useMirrorDock must be used inside MirrorDockProvider");
  }
  return ctx;
}
